import { ORGANIZATION_LIMIT_REACHED_MESSAGE } from '@/config/plans'
import { getDefaultOrganizationId } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { hasReachedOrganizationCreationLimit, useIsOwner } from '@/utils/permissions'
import { ChevronRight } from 'lucide-react'
import { SwitcherCreateRow, SwitcherPane } from './SwitcherPane'
import { useOrganizations, usePinDefault } from './useSwitcherData'

interface OrganizationPaneProps {
	currentOrgId: string | null
	onOrganizationSelect: (orgId: string) => void
	onCreateOrgClick: () => void
	/** Rows get a chevron and report hover, so the caller can show that org's projects beside the list. */
	showProjects?: boolean
	hoveredOrgId?: string | null
	onOrganizationHover?: (orgId: string) => void
	/** Right arrow on a row, so the keyboard can reach the projects shown for it. */
	onEnterProjects?: () => void
}

export function OrganizationPane({
	currentOrgId,
	onOrganizationSelect,
	onCreateOrgClick,
	showProjects = false,
	hoveredOrgId,
	onOrganizationHover,
	onEnterProjects,
}: OrganizationPaneProps) {
	const isOwner = useIsOwner()
	const limitReached = useAuthStore(s => !!s.user && hasReachedOrganizationCreationLimit(s.user))
	const pinnedId = useAuthStore(s => getDefaultOrganizationId(s.user))
	const organizations = useOrganizations()
	const { canPin, pin, isPinning } = usePinDefault('organization')

	return (
		<SwitcherPane
			testId='org-switcher'
			noun='organization'
			items={organizations.map(org => ({
				id: org.id,
				name: org.name,
				keywords: org.projectNames,
				selected: org.id === currentOrgId,
				hovered: org.id === hoveredOrgId,
				onSelect: () => onOrganizationSelect(org.id),
				onHover: () => onOrganizationHover?.(org.id),
				onKeyDown: e => {
					if (showProjects && e.key === 'ArrowRight') {
						e.preventDefault()
						onEnterProjects?.()
					}
				},
				trailing: showProjects && <ChevronRight className='icon-alone text-muted-foreground' strokeWidth={2} />,
			}))}
			pin={
				canPin
					? {
							pinnedId,
							disabled: isPinning,
							onToggle: (id, pinned) =>
								pin(current => ({ ...current, default_organization: pinned ? undefined : id }), pinned),
						}
					: undefined
			}
			footer={
				isOwner && (
					<SwitcherCreateRow
						testId='org-switcher-create'
						label='New organization'
						onClick={onCreateOrgClick}
						disabledReason={limitReached && ORGANIZATION_LIMIT_REACHED_MESSAGE}
					/>
				)
			}
		/>
	)
}
